import axios from 'axios';
import { API_BASE } from './config';

/**
 * Fetch transactions with pagination and filtering
 * @param filters Filter criteria
 * @param limit Maximum number of transactions to return
 * @param skip Number of transactions to skip (for pagination)
 * @returns Transactions matching the criteria
 */
export const fetchTransactions = async (
  filters: any = {},
  limit: number = 50,
  skip: number = 0
) => {
  const response = await axios.post(`${API_BASE}/transaction/GetAllTransaction`, {
    filters,
    limit,
    skip
  });
  return response.data;
};

/**
 * Check transaction status
 * @param txid Transaction ID
 * @param network Network name
 * @param is_evm Whether the network is EVM-compatible
 * @returns Transaction status
 */
export const checkTransactionStatus = async (
  txid: string,
  network: string,
  is_evm: number
) => {
  const response = await axios.post(`${API_BASE}/transaction/TxStatus`, {
    txid,
    network,
    is_evm
  });
  return response.data;
};

/**
 * Create and broadcast a transaction
 * @param data Transaction data
 * @returns Transaction result
 */
export const sendCrypto = async (data: {
  provider: string;
  token: string;
  amount: string;
  to: string;
  privatekey: string;
  from?: string;
  is_evm: number;
  tag?: string;
  publicKey?: string;
  speed?: 'slow' | 'medium' | 'fast';
}) => {
  try {
    console.log('Sending transaction with data:', {
      ...data,
      privatekey: '[REDACTED]', // Don't log the private key
      amount: data.amount,
      to: data.to,
      provider: data.provider
    });

    const response = await axios.post(`${API_BASE}/transaction/SendCrypto`, data);

    console.log('Transaction API response:', response.data);

    // Return the response data as is
    return response.data;
  } catch (error) {
    console.error('Error in sendCrypto API call:', error);

    // If it's an Axios error with a response, return the error response
    if (axios.isAxiosError(error) && error.response) {
      console.error('API error response:', error.response.data);
      return error.response.data;
    }

    // Otherwise, return a formatted error
    return {
      status: false,
      error: {
        type: 'API_ERROR',
        message: error instanceof Error ? error.message : 'Unknown error',
        details: { originalError: error }
      }
    };
  }
};

/**
 * Create a withdrawal transaction
 * @param data Withdrawal data
 * @returns Withdrawal result
 */
export const withdraw = async (data: {
  userid: number;
  amount: number;
  to: string;
  coin: string;
  network: string;
  is_evm: number;
  tag?: string;
}) => {
  const response = await axios.post(`${API_BASE}/transaction/Withdraw`, data);
  return response.data;
};

/**
 * Update withdrawal status
 * @param data Status update data
 * @returns Update result
 */
export const updateWithdrawStatus = async (data: {
  txid: string;
  status: string;
  networkresult?: string;
}) => {
  const response = await axios.post(`${API_BASE}/transaction/UpdateWithdrawStatus`, data);
  return response.data;
};

/**
 * Fetch deposit transactions
 * @param filters Filter criteria
 * @param limit Maximum number of transactions to return
 * @param skip Number of transactions to skip (for pagination)
 * @returns Deposit transactions
 */
export const fetchDepositTransactions = async (
  filters: any = {},
  limit: number = 50,
  skip: number = 0
) => {
  const response = await axios.post(`${API_BASE}/deposit/list`, {
    filters,
    limit,
    skip
  });
  return response.data;
};

/**
 * Fetch withdrawal transactions
 * @param filters Filter criteria
 * @param limit Maximum number of transactions to return
 * @param skip Number of transactions to skip (for pagination)
 * @returns Withdrawal transactions
 */
export const fetchWithdrawalTransactions = async (
  filters: any = {},
  limit: number = 50,
  skip: number = 0
) => {
  const response = await axios.post(`${API_BASE}/withdrawal/list`, {
    filters,
    limit,
    skip
  });
  return response.data;
};

/**
 * Process a withdrawal transaction
 * @param id Withdrawal transaction ID
 * @returns Process result
 */
export const processWithdrawal = async (id: string) => {
  const response = await axios.post(`${API_BASE}/withdrawal/process/${id}`);
  return response.data;
};

/**
 * Speed up a withdrawal transaction
 * @param id Withdrawal transaction ID
 * @param gasPriceMultiplier Multiplier for the gas price (e.g., 1.5 for 50% higher)
 * @returns Speed up result
 */
export const speedUpWithdrawal = async (id: string, gasPriceMultiplier: number = 1.5) => {
  const response = await axios.post(`${API_BASE}/withdrawal/speed-up/${id}`, {
    gasPriceMultiplier
  });
  return response.data;
};

/**
 * Cancel a withdrawal transaction
 * @param id Withdrawal transaction ID
 * @param gasPriceMultiplier Multiplier for the gas price (e.g., 1.5 for 50% higher)
 * @returns Cancel result
 */
export const cancelWithdrawal = async (id: string, gasPriceMultiplier: number = 1.5) => {
  const response = await axios.post(`${API_BASE}/withdrawal/cancel/${id}`, {
    gasPriceMultiplier
  });
  return response.data;
};

/**
 * Fetch transaction details by ID
 * @param id Transaction ID
 * @returns Transaction details
 */
export const fetchTransactionById = async (id: string) => {
  try {
    // First try to fetch from deposit endpoint
    const depositResponse = await axios.get(`${API_BASE}/deposit/${id}`);
    if (depositResponse.data.success && depositResponse.data.deposit) {
      return {
        success: true,
        data: depositResponse.data.deposit
      };
    }
  } catch (error) {
    console.log('Not a deposit transaction, trying withdrawal endpoint');
  }

  try {
    // If not a deposit, try withdrawal endpoint
    const withdrawalResponse = await axios.get(`${API_BASE}/withdrawal/${id}`);
    if (withdrawalResponse.data.success && withdrawalResponse.data.withdrawal) {
      return {
        success: true,
        data: withdrawalResponse.data.withdrawal
      };
    }
  } catch (error) {
    console.log('Not a withdrawal transaction, trying transaction endpoint');
  }

  try {
    // If not a deposit or withdrawal, try general transaction endpoint
    const transactionResponse = await axios.get(`${API_BASE}/transaction/${id}`);
    return transactionResponse.data;
  } catch (error) {
    throw new Error('Transaction not found');
  }
};
